import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CheckCircle2, XCircle, Building2 } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { AdminRoute } from '../../components/admin/AdminRoute';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { AdminMetrics } from './AdminMetrics';
import { supabase } from '../../lib/supabase';

interface GymBillingStatus {
  company_id: string;
  company_name: string;
  billing_settings_configured: boolean;
  stripe_connect_account_id: string | null;
  stripe_connect_account_status: string | null;
  stripe_connect_onboarding_complete: boolean;
  stripe_connect_account_balance: number | null; // Balance in cents (negative = negative balance)
  saas_subscription_status: string | null;
  saas_subscription_plan_type: string | null;
  saas_subscription_price: number | null;
  saas_subscription_billing_cycle: string | null;
  enable_billing: boolean;
  enable_demo: boolean;
  created_at: string;
}

interface CompanyWithRelations {
  id: string;
  name: string;
  created_at: string;
  enable_billing: boolean;
  enable_demo: boolean;
  stripe_connect_account_id: string | null;
  stripe_connect_account_status: string | null;
  stripe_connect_onboarding_complete: boolean;
  stripe_connect_account_balance: number | null;
  subscription_billing_settings: Array<{ id: string }> | null;
  saas_subscriptions: Array<{
    status: string;
    plan_type: string;
    price: number;
    billing_cycle: string;
  }> | null;
}

export default function StripeManagementPage() {
  const navigate = useNavigate();
  const { adminUser, isLoading: authLoading } = useAdminAuth();
  const [gyms, setGyms] = useState<GymBillingStatus[]>([]);
  const [filteredGyms, setFilteredGyms] = useState<GymBillingStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [connectStatusFilter, setConnectStatusFilter] = useState<string>('all');

  useEffect(() => {
    if (adminUser) {
      loadGymBillingStatus();
    }
  }, [adminUser]);

  const filterGyms = useCallback(() => {
    const filtered = gyms.filter((gym) => {
      const matchesSearch = gym.company_name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || gym.saas_subscription_status === statusFilter;
      const matchesConnect = connectStatusFilter === 'all' || gym.stripe_connect_account_status === connectStatusFilter;
      return matchesSearch && matchesStatus && matchesConnect;
    });
    setFilteredGyms(filtered);
  }, [gyms, searchTerm, statusFilter, connectStatusFilter]);

  useEffect(() => {
    filterGyms();
  }, [filterGyms]);

  const loadGymBillingStatus = async () => {
    setIsLoading(true);
    try {
      // Fetch all companies with their billing and subscription info
      const { data: companies, error: companiesError } = await supabase
        .from('companies')
        .select(`
          id,
          name,
          created_at,
          enable_billing,
          enable_demo,
          stripe_connect_account_id,
          stripe_connect_account_status,
          stripe_connect_onboarding_complete,
          stripe_connect_account_balance,
          subscription_billing_settings (
            id
          ),
          saas_subscriptions (
            status,
            plan_type,
            price,
            billing_cycle
          )
        `)
        .order('created_at', { ascending: false });

      if (companiesError) throw companiesError;

      if (!companies) {
        setGyms([]);
        return;
      }

      const gymStatuses: GymBillingStatus[] = companies.map((company) => {
        const typedCompany = company as unknown as CompanyWithRelations;
        const saasSub = typedCompany.saas_subscriptions && typedCompany.saas_subscriptions.length > 0 
          ? typedCompany.saas_subscriptions[0] 
          : null;

        return {
          company_id: typedCompany.id,
          company_name: typedCompany.name || 'Unnamed Company',
          billing_settings_configured: !!typedCompany.subscription_billing_settings,
          stripe_connect_account_id: typedCompany.stripe_connect_account_id,
          stripe_connect_account_status: typedCompany.stripe_connect_account_status,
          stripe_connect_onboarding_complete: typedCompany.stripe_connect_onboarding_complete || false,
          stripe_connect_account_balance: typedCompany.stripe_connect_account_balance || null,
          saas_subscription_status: saasSub?.status || null,
          saas_subscription_plan_type: saasSub?.plan_type || null,
          saas_subscription_price: saasSub?.price || null,
          saas_subscription_billing_cycle: saasSub?.billing_cycle || null,
          enable_billing: typedCompany.enable_billing !== false,
          enable_demo: typedCompany.enable_demo || false,
          created_at: typedCompany.created_at,
        };
      });

      setGyms(gymStatuses);
    } catch (error) {
      console.error('Error loading gym billing status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string | null) => {
    if (!status) return <span className="px-2 py-1 rounded text-xs bg-gray-500 text-white">No Subscription</span>;
    
    const statusColors: Record<string, string> = {
      active: 'bg-green-500',
      trialing: 'bg-[#00D9FF]',
      past_due: 'bg-yellow-500',
      cancelled: 'bg-gray-500',
      unpaid: 'bg-red-500',
    };

    return (
      <span className={`px-2 py-1 rounded text-xs text-white ${statusColors[status] || 'bg-gray-500'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getConnectStatusBadge = (status: string | null, complete: boolean) => {
    if (!status) return <span className="px-2 py-1 rounded text-xs bg-gray-500 text-white">Not Setup</span>;
    
    if (status === 'active' && complete) {
      return <span className="px-2 py-1 rounded text-xs bg-green-500 text-white">Active</span>;
    } else if (status === 'pending') {
      return <span className="px-2 py-1 rounded text-xs bg-yellow-500 text-white">Pending</span>;
    } else if (status === 'restricted') {
      return <span className="px-2 py-1 rounded text-xs bg-red-500 text-white">Restricted</span>;
    } else {
      return <span className="px-2 py-1 rounded text-xs bg-gray-500 text-white">{status}</span>;
    }
  };

  if (authLoading || isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421]" />
        </div>
      </AdminLayout>
    );
  }

  if (!adminUser) {
    return null; // Will redirect
  }

  return (
    <AdminRoute>
      <AdminLayout>
        <div className="space-y-6 min-h-0">
        <div className="mb-4 sm:mb-5 md:mb-6">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#EBFFEE] mb-1 sm:mb-2">Stripe Management</h1>
          <p className="text-xs sm:text-sm md:text-base text-[#A9A3A3]">Manage gym billing settings, Stripe Connect accounts, and subscriptions</p>
        </div>

        {/* Metrics Overview */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-[#EBFFEE] mb-4">Metrics Overview</h2>
          <AdminMetrics />
        </section>

        {/* Filters */}
        <Card className="bg-[#221F1F] border-[#504A4A] mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A9A3A3] w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search by company name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Subscription Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="no_subscription">No Subscription</option>
                  <option value="past_due">Past Due</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Connect Status</label>
                <select
                  value={connectStatusFilter}
                  onChange={(e) => setConnectStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="not_setup">Not Setup</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Gyms List */}
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE]">Gyms ({filteredGyms.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredGyms.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="mx-auto h-12 w-12 text-[#A9A3A3] mb-4" />
                <p className="text-[#A9A3A3]">No gyms found matching your filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#504A4A]">
                      <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Company</th>
                      <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Billing Settings</th>
                      <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Stripe Connect</th>
                      <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">SaaS Subscription</th>
                      <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Feature Toggles</th>
                      <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGyms.map((gym) => (
                      <tr key={gym.company_id} className="border-b border-[#504A4A] hover:bg-[#363232]">
                        <td className="py-4 px-4">
                          <div className="font-medium text-[#EBFFEE]">{gym.company_name}</div>
                          <div className="text-sm text-[#A9A3A3]">ID: {gym.company_id.slice(0, 8)}...</div>
                        </td>
                        <td className="py-4 px-4">
                          {gym.billing_settings_configured ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500" />
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {getConnectStatusBadge(gym.stripe_connect_account_status, gym.stripe_connect_onboarding_complete)}
                        </td>
                        <td className="py-4 px-4">
                          {gym.stripe_connect_account_balance !== null ? (
                            <div className={`font-medium ${
                              gym.stripe_connect_account_balance < 0 
                                ? 'text-red-500' 
                                : gym.stripe_connect_account_balance > 0 
                                ? 'text-green-500' 
                                : 'text-[#A9A3A3]'
                            }`}>
                              ${(gym.stripe_connect_account_balance / 100).toFixed(2)}
                              {gym.stripe_connect_account_balance < 0 && (
                                <span className="ml-1 text-xs">⚠️</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-[#A9A3A3]">—</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {gym.saas_subscription_status ? (
                            <div>
                              {getStatusBadge(gym.saas_subscription_status)}
                              <div className="text-sm text-[#A9A3A3] mt-1">
                                {gym.saas_subscription_plan_type && (
                                  <div>{gym.saas_subscription_plan_type.charAt(0).toUpperCase() + gym.saas_subscription_plan_type.slice(1)}</div>
                                )}
                                {gym.saas_subscription_price && (
                                  <div>${gym.saas_subscription_price.toFixed(2)}/{gym.saas_subscription_billing_cycle || 'mo'}</div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-[#A9A3A3]">No subscription</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex flex-col gap-1">
                            {gym.enable_billing ? (
                              <span className="text-xs text-green-500">Billing Enabled</span>
                            ) : (
                              <span className="text-xs text-red-500">Billing Disabled</span>
                            )}
                            {gym.enable_demo && (
                              <span className="text-xs text-[#00D9FF]">Demo Mode</span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <button
                            onClick={() => navigate(`/admin/rollops-subscriptions?companyId=${gym.company_id}`)}
                            className="text-rollops-accent hover:text-[#EBFFEE] text-sm font-medium hover:underline"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      </AdminLayout>
    </AdminRoute>
  );
}

